import { Project, User, UserProject, UserProjectUserNameEmail } from "../types/admin";
import axios from "axios";
import { BaseApi } from "./BaseApi";
import { LazyLoadingApiResponse } from "../types/api";

export class AdminApi extends BaseApi {
    

    public async getUsers(
        page: number = 0, 
        limit: number = 10, 
        names?: string[], 
        projects?: string[], 
        beneficiaries?: string[], 
        azureIds?: string[],
        emails?: string[],
        sortField?: string,
        sortDirection?: 'asc' | 'desc'
    ): Promise<any> {
        await this.refreshToken();
        try {
            // Build base URL
            let url = `${this.config.apiUrl}/Admin/Users?page=${page}&limit=${limit}`;
            
            // Add filters if present
            if (names && names.length > 0) {
                names.forEach(name => {
                    url += `&names=${encodeURIComponent(name)}`;
                });
            }
            
            if (projects && projects.length > 0) {
                projects.forEach(project => {
                    url += `&projects=${encodeURIComponent(project)}`;
                });
            }
            
            if (beneficiaries && beneficiaries.length > 0) {
                beneficiaries.forEach(beneficiary => {
                    url += `&beneficiaries=${encodeURIComponent(beneficiary)}`;
                });
            }
            
            if (azureIds && azureIds.length > 0) {
                azureIds.forEach(azureId => {
                    url += `&azureIds=${encodeURIComponent(azureId)}`;
                });
            }
    
            if (emails && emails.length > 0) {
                emails.forEach(email => {
                    url += `&emails=${encodeURIComponent(email)}`;
                });
            }
            
            // Add sorting parameters if present
            if (sortField) {
                url += `&sortField=${encodeURIComponent(sortField)}`;
                
                if (sortDirection) {
                    url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
                }
            }
            
            const response = await axios.get(
                url,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    public async deleteUser(userId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.delete(
                this.config.apiUrl + "/Admin/User/" + userId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    public async getUser(userId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/User/" + userId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    public async updateUser(user: User): Promise<string> {
        await this.refreshToken();
        try {
            await axios.patch(
                this.config.apiUrl + "/Admin/Webapp/User/" + user.id,
                {
                    email: user.email,
                    name: user.name,
                    isAdmin: user.isAdmin,
                    azureAdId : user.azureAdId
                },
                this.getDefaultHeaders()
            );
            return "success";
        } catch (error) {
            throw error;
        }
    }

    public async createUser(user: User): Promise<string> {
        await this.refreshToken();
        try {
            await axios.post(
                this.config.apiUrl + "/Admin/Webapp/User/",
                {
                    email: user.email,
                    name: user.name,
                    isAdmin: user.isAdmin
                },
                this.getDefaultHeaders()
            );
            return "success";
        } catch (error) {
            throw error;
        }
    }

    public async getProjects(): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/Projects",
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    public async getProject(projectId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/Project/" + projectId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    public async updateProject(project: Project): Promise<string> {
        await this.refreshToken();
        try {
            await axios.patch(
                this.config.apiUrl + "/Admin/Project/" + project.id,
                {
                    code: project.code,
                    name: project.name,
                    description: project.description,
                    cordaStatus: project.cordaStatus,
                },
                this.getDefaultHeaders()
            );
            return "success";
        } catch (error) {
            throw error;
        }
    }

    public async createProject(project: Project): Promise<string> {
        await this.refreshToken();
        try {
            await axios.post(
                this.config.apiUrl + "/Admin/Project/",
                {
                    code: project.code,
                    name: project.name,
                    description: project.description,
                    cordaStatus: project.cordaStatus,
                    isCbe: project.isCbe,
                },
                this.getDefaultHeaders()
            );
            return "success";
        } catch (error) {
            throw error;
        }
    }
    

    public async deleteProject(projectId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.delete(
                this.config.apiUrl + "/Admin/Project/" + projectId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    public async getProjectsExtract(projectStatus : number ): Promise<any>
    {
        await this.refreshToken();
        try {
            const response = await axios.get(
                `${this.config.apiUrl}/Admin/StatusExtract/${projectStatus}`,
                {
                    ...this.getDefaultHeaders(),
                    responseType: 'blob'
                }
            );
    
            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'projects.xlsx'; // Default name if not found in headers
    
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
    
            // Create Blob and trigger download
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url); // Free memory
    
            return {
                success: true,
                filename: filename
            };
        } catch (error) {
            throw error;
        }
    }
    
    public async getSelectedProjectsExtract( projectIds?: string[]): Promise<any>
    {
        await this.refreshToken();
        try {
            const response = await axios.post(
                `${this.config.apiUrl}/Admin/SelectedExtract/`,
                { projectIds: projectIds || [] },
                {
                    ...this.getDefaultHeaders(),
                    responseType: 'blob'
                }
            );
    
            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'projects.xlsx'; // Default name if not found in headers
    
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
    
            // Create Blob and trigger download
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url); // Free memory
    
            return {
                success: true,
                filename: filename
            };
        } catch (error) {
            throw error;
        }
    }

    public async getLastValidatedExtract(projectStatus: number): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                `${this.config.apiUrl}/Admin/LastValidatedExtract/${projectStatus}`,
                {
                    ...this.getDefaultHeaders(),
                    responseType: 'blob'
                }
            );

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'projects_lastvalidated.xlsx'; // Default name if not found in headers

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            // Create Blob and trigger download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url); // Free memory

            return {
                success: true,
                filename: filename
            };
        } catch (error) {
            throw error;
        }
    }

    public async getSelectedLogExtract( projectIds?: string[]): Promise<any>
    {
        await this.refreshToken();
        try {
            const response = await axios.post(
                `${this.config.apiUrl}/Admin/SelectedExtractLog/`,
                { projectIds: projectIds || [] },
                {
                    ...this.getDefaultHeaders(),
                    responseType: 'blob'
                }
            );
    
            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'projects.xlsx'; // Default name if not found in headers
    
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
    
            // Create Blob and trigger download
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url); // Free memory
    
            return {
                success: true,
                filename: filename
            };
        } catch (error) {
            throw error;
        }
    }

    public async getUserProjects(projectId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/UserProject/" + projectId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    
    public async deleteUserProject(projectId: string, userId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.delete(
                this.config.apiUrl + "/Admin/UserProject/" + projectId + "/" + userId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async createUserProject(userProject : UserProject): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.post(
                this.config.apiUrl + "/Admin/UserProjectWebApp",
                {
                    projectId : userProject.projectId, 
                    role : userProject.role, 
                    userId : userProject.userId
                },
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    
    public async getUsersWithEmail(): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/UsersEmail",
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async getProjectsWithName(): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/ProjectsName",
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    
    // Lazy loading methods for better performance with large datasets
    public async getProjectNames(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/ProjectsName', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async getProjectCodes(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/ProjectsCode', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async getProjectCordaStatus(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/ProjectsCordaStatus', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    

    public async cloneQuestionnaire(questionnaireId: string): Promise<any> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/CloneQuestionnaire/" + questionnaireId,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    
    // User search methods with lazy loading
    public async getUserNames(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/UserNames', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async getUserEmails(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/UserEmails', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async getBeneficiaries(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/Beneficiaries', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    public async getUserAzureAdId(query: string, page: number, limit: number): Promise<LazyLoadingApiResponse> {
        await this.refreshToken();
        try {
            const headers = this.getDefaultHeaders();
            const response = await axios.get(
                this.config.apiUrl + '/SearchLazyLoading/AzureId', 
                {
                    headers: headers.headers,
                    params: {
                        query,
                        page,
                        limit
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    
    // Get roles with filtering and sorting options
    public async getRoles(
        page: number = 0, 
        limit: number = 10, 
        roles?: number[],
        projects?: string[], 
        users?: string[],
        sortField?: string,
        sortDirection?: 'asc' | 'desc'
    ): Promise<any> {
        await this.refreshToken();
        try {
            // Build base URL
            let url = `${this.config.apiUrl}/Admin/Roles?page=${page}&limit=${limit}`;
            
            // Add filters if present
            if (projects && projects.length > 0) {
                projects.forEach(project => {
                    url += `&projects=${encodeURIComponent(project)}`;
                });
            }
            
            if (users && users.length > 0) {
                users.forEach(user => {
                    url += `&users=${encodeURIComponent(user)}`;
                });
            }
            
            if (roles && roles.length > 0) {
                roles.forEach(role => {
                    url += `&roles=${encodeURIComponent(role)}`;
                });
            }
            
            // Add sorting parameters if present
            if (sortField) {
                url += `&sortField=${encodeURIComponent(sortField)}`;
                
                if (sortDirection) {
                    url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
                }
            }
            
            const response = await axios.get(
                url,
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    
    // Export users to CSV with filtering and sorting options
    public async exportUsersCsv(
        names?: string[], 
        projects?: string[], 
        beneficiaries?: string[], 
        azureIds?: string[],
        emails?: string[],
        sortField?: string,
        sortDirection?: 'asc' | 'desc'
    ): Promise<void> {
        await this.refreshToken();
        try {
            // Build base URL for CSV export
            let url = `${this.config.apiUrl}/Export/Users`;
            
            // Create URLSearchParams object to properly handle parameters
            const params = new URLSearchParams();
            
            // Add filters if present
            if (names && names.length > 0) {
                names.forEach(name => {
                    params.append('names', name);
                });
            }
            
            if (projects && projects.length > 0) {
                projects.forEach(project => {
                    params.append('projects', project);
                });
            }
            
            if (beneficiaries && beneficiaries.length > 0) {
                beneficiaries.forEach(beneficiary => {
                    params.append('beneficiaries', beneficiary);
                });
            }
            
            if (azureIds && azureIds.length > 0) {
                azureIds.forEach(azureId => {
                    params.append('azureIds', azureId);
                });
            }

            if (emails && emails.length > 0) {
                emails.forEach(email => {
                    params.append('emails', email);
                });
            }
            
            // Add sorting parameters if present
            if (sortField) {
                params.append('sortField', sortField);
                
                if (sortDirection) {
                    params.append('sortDirection', sortDirection);
                }
            }
            
            // Add parameters to URL
            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
            
            // Get base headers
            const defaultHeaders = this.getDefaultHeaders();
            
            // Configure options for axios
            const requestConfig = {
                headers: {
                    ...defaultHeaders.headers,
                    'Accept': 'text/csv'
                },
                responseType: 'blob' as 'blob' // Type assertion for TypeScript
            };
            
            // Make the GET request
            const response = await axios.get(url, requestConfig);
            
            // Create a URL object for the blob
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            
            // Create a temporary <a> element to trigger the download
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Extract filename from Content-Disposition header if it exists
            let filename = 'users_export.csv';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            throw error;
        }
    }
    
    
    // Export roles to CSV with filtering and sorting options
    public async exportRolesCsv(
        projects?: string[], 
        users?: string[], 
        roles?: number[],
        sortField?: string,
        sortDirection?: 'asc' | 'desc'
    ): Promise<void> {
        await this.refreshToken();
        try {
            // Build base URL for CSV export
            let url = `${this.config.apiUrl}/Export/Roles`;
            
            // Create URLSearchParams object to properly handle parameters
            const params = new URLSearchParams();
            
            // Add filters if present
            if (projects && projects.length > 0) {
                projects.forEach(project => {
                    params.append('projects', project);
                });
            }
            
            if (users && users.length > 0) {
                users.forEach(user => {
                    params.append('users', user);
                });
            }
            
            if (roles && roles.length > 0) {
                roles.forEach(role => {
                    params.append('roles', role.toString());
                });
            }
            
            // Add sorting parameters if present
            if (sortField) {
                params.append('sortField', sortField);
                
                if (sortDirection) {
                    params.append('sortDirection', sortDirection);
                }
            }
            
            // Add parameters to URL
            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
            
            // Get base headers
            const defaultHeaders = this.getDefaultHeaders();
            
            // Configure options for axios
            const requestConfig = {
                headers: {
                    ...defaultHeaders.headers,
                    'Accept': 'text/csv'
                },
                responseType: 'blob' as 'blob' // Type assertion for TypeScript
            };
            
            // Make the GET request
            const response = await axios.get(url, requestConfig);
            
            // Create a URL object for the blob
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            
            // Create a temporary <a> element to trigger the download
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Extract filename from Content-Disposition header if it exists
            let filename = 'roles_export.csv';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error exporting roles CSV:', error);
            throw error;
        }
    }
    
    // Upload tooltips and placeholders file
    public async uploadTooltipsPlaceholders(file: File): Promise<any> {
        await this.refreshToken();
        try {
            // Create FormData to send the file
            const formData = new FormData();
            formData.append('file', file);
    
            // Make the request
            const response = await axios.post(
                `${this.config.apiUrl}/Import/UploadTooltipsPlaceholders`,
                formData,
                {
                    headers: {
                        ...this.getDefaultHeaders().headers,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error uploading tooltips file:', error);
            throw error;
        }
    }

    // Upload zip with CORDA updates
    public async uploadCORDAFiles(file: File): Promise<any> {
        await this.refreshToken();
        try {
            // Create FormData to send the file
            const formData = new FormData();
            formData.append('file', file);

            // Make the request
            const response = await axios.post(
                `${this.config.apiUrl}/Import/UploadCORDA`,
                formData,
                {
                    headers: {
                        ...this.getDefaultHeaders().headers,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error uploading CORDA file:', error);
            throw error;
        }
    }
    // Export projects to CSV
    public async exportProjectsCsv(): Promise<void> {
        await this.refreshToken();
        try {
            let url = `${this.config.apiUrl}/Export/Projects`;

            const defaultHeaders = this.getDefaultHeaders();
            
            const requestConfig = {
                headers: {
                    ...defaultHeaders.headers,
                    'Accept': 'text/csv'
                },
                responseType: 'blob' as 'blob' 
            };
            
            const response = await axios.get(url, requestConfig);
            
            // Create a URL object for the blob
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            
            // Create a temporary <a> element to trigger the download
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Extract filename from Content-Disposition header if it exists
            let filename = 'project_export.csv';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            throw error;
        }
    }
    public async getCurrentUser(): Promise<User> {
        await this.refreshToken();
        try {
            const response = await axios.get(
                this.config.apiUrl + "/Admin/CurrentUser", 
                this.getDefaultHeaders()
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}